"use client";

import { useEffect } from "react";

export default function GlobalError({ error, reset }) {
  useEffect(() => {
    // Enhanced error logging for server components
    const errorInfo = {
      type: "global-error",
      message: error.message || "Unknown error",
      stack: error.stack || "No stack trace available",
      digest: error.digest || "No digest available",
      name: error.name || "Error",
      cause: error.cause || null,
      timestamp: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "N/A",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "N/A",
    };

    // Log to console for debugging
    console.error("🚨 GLOBAL ERROR CAUGHT:", errorInfo);
    console.error("🔍 Full error object:", error);

    // Log error for debugging

    // Also send error notification similar to regular error.js
    const sendErrorNotification = async () => {
      try {
        console.log("🔔 [Global Error] Sending notification");

        const errorMessage = {
          text: "🚨 Global Frontend Error Occurred",
          blocks: [
            {
              type: "header",
              text: {
                type: "plain_text",
                text: "🚨 Global Error Alert",
              },
            },
            {
              type: "section",
              fields: [
                {
                  type: "mrkdwn",
                  text: `*Error Type:* Global Error Handler`,
                },
                {
                  type: "mrkdwn",
                  text: `*Time:* ${errorInfo.timestamp}`,
                },
              ],
            },
            {
              type: "section",
              text: {
                type: "mrkdwn",
                text: `*Error Details:*\n*Type:* ${errorInfo.name}\n*Message:* ${errorInfo.message}`,
              },
            },
            {
              type: "section",
              text: {
                type: "mrkdwn",
                text: `*Digest:* ${errorInfo.digest}`,
              },
            },
            {
              type: "section",
              fields: [
                {
                  type: "mrkdwn",
                  text: `*URL:* ${errorInfo.url}`,
                },
                {
                  type: "mrkdwn",
                  text: `*User Agent:* ${errorInfo.userAgent.slice(0, 100)}...`,
                },
              ],
            },
            {
              type: "section",
              text: {
                type: "mrkdwn",
                text: `*Stack Trace:*\n\`\`\`${errorInfo.stack.slice(0, 800)}\`\`\``,
              },
            },
          ],
        };

        const response = await fetch("/api/error-notification", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(errorMessage),
        });

        if (response.ok) {
          console.log("✅ [Global Error] Notification sent successfully");
        } else {
          console.error(
            "❌ [Global Error] Failed to send notification:",
            response.status
          );
        }
      } catch (notificationError) {
        console.error(
          "❌ [Global Error] Notification error:",
          notificationError
        );
      }
    };

    sendErrorNotification();
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
        <h1 className="text-2xl font-bold text-red-600 mb-4">
          Critical Error!
        </h1>
        <p className="text-gray-600 mb-4">
          The application encountered a critical error and needs to restart.
        </p>

        {error.digest && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
            <p className="text-sm text-red-800">
              <strong>Error ID:</strong> {error.digest}
            </p>
            <p className="text-xs text-red-600 mt-1">
              This ID can help our team investigate the issue.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => reset()}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try again
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Go to Home
          </button>
        </div>
      </div>
    </div>
  );
}
